import { isGlMapHideOperation } from '@festina/configuration-general-types/configuration'
import { STANDARD_BRANCH } from '@festina-life/api'
import { errorMessageToastContent } from '@festina-life/components'
import { dateUtils, type Optional, urlUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { toast } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Dropdown } from '@festina-life/ui/structure'

import { useConfigurationContext } from '../../context/configurationContext'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useMutateConvertGlMapCreateOperationToExcel } from '../../uiModels/api/glMaps/useMutateConvertGlMapCreateOperationToExcel'
import { useMutateFetchStandardGlMapExcel } from '../../uiModels/api/glMaps/useMutateFetchStandardGlMapExcel'
import type { IndexedGlMapOperationWithMetaData } from '../../uiModels/glMapOperations/types'
import { downloadJsonFile } from '../../util/downloadJsonFile'
import { glMapExcelFileName, glMapJsonFileName } from './helpers'

type Props = {
  /** Version in scope, if any exists at the current view date. */
  glMapOperation: Optional<IndexedGlMapOperationWithMetaData>
}

/**
 * Exports the GL-map version currently in scope, either as the Excel document it is
 * authored in, or as the in-out entity the portal holds, which can be edited and uploaded back.
 */
export const GlMapDownloadVersionButton = ({ glMapOperation }: Props) => {
  const translate = useTranslate()
  const { branch } = useNavigationParams()
  const { viewAt } = useConfigurationContext()
  const { mutateAsync: convertToExcel, isPending: isConverting } =
    useMutateConvertGlMapCreateOperationToExcel()
  const { mutateAsync: fetchStandardExcel, isPending: isFetching } =
    useMutateFetchStandardGlMapExcel()

  const operation = glMapOperation?.internalOperation

  if (operation && isGlMapHideOperation(operation)) {
    return (
      <Button
        startIcon="ArrowDownTray"
        tip={translate('glMapViewer.topBar.actions.downloadVersion.hidden')}
        disabled
      >
        {translate('glMapViewer.topBar.actions.downloadVersion')}
      </Button>
    )
  } else if (operation?.glMap && operation.glMap.mappingId) {
    const inOutEntity = operation.glMap
    const mappingId = operation.glMap.mappingId // taken from operation.glMap for the type narrowing to ensure it's defined

    return (
      <Dropdown
        element={
          <Button
            startIcon="ArrowDownTray"
            endIcon="ChevronDown"
            isLoading={isConverting || isFetching}
          >
            {translate('glMapViewer.topBar.actions.downloadVersion')}
          </Button>
        }
        items={[
          {
            element: translate('documentFormat.excel'),
            onClick: async () => {
              try {
                const excelDocument =
                  branch === STANDARD_BRANCH
                    ? await fetchStandardExcel({
                        mappingId,
                        at: dateUtils.dateAsServerStr(
                          inOutEntity.from ?? viewAt,
                        ),
                      })
                    : await convertToExcel({ glMapCreateOperation: operation })

                urlUtils.downloadAsFile(
                  excelDocument,
                  glMapExcelFileName(mappingId, inOutEntity.from),
                )
              } catch (error) {
                toast(errorMessageToastContent({ error, translate }))
              }
            },
          },
          {
            element: translate('documentFormat.json'),
            onClick: () => {
              downloadJsonFile(
                inOutEntity,
                glMapJsonFileName(mappingId, inOutEntity.from),
              )
            },
          },
        ]}
      />
    )
  }

  return null
}
